import { createContext, useContext, useMemo, type ReactNode } from "react";
import { usePrimaryWatchlist } from "@/hooks/useWatchlist";
import type { RegionOption } from "@/hooks/useDvDetailView";

// ─── Types ──────────────────────────────────────────────────────────────────

interface DefaultFilters {
  cattleType: string;
  weightClass: string;
  region: RegionOption;
  /** True once the watchlist query has resolved (even if no watchlist exists) */
  ready: boolean;
}

const FALLBACKS: DefaultFilters = {
  cattleType: "Steers",
  weightClass: "wt_600_699",
  region: "All",
  ready: false,
};

// ─── Context ────────────────────────────────────────────────────────────────

const DefaultFiltersContext = createContext<DefaultFilters>(FALLBACKS);

/**
 * Reads the user's primary watchlist and exposes its cattle_type,
 * weight_class, and region as default filter values for every tool.
 *
 * Mount once inside Layout (below auth guard) so every protected
 * page can call useDefaultFilters() instead of hardcoding "Steers".
 */
export function DefaultFiltersProvider({ children }: { children: ReactNode }) {
  const { data: watchlist, isLoading } = usePrimaryWatchlist();

  const value: DefaultFilters = useMemo(() => ({
    cattleType: watchlist?.cattle_type ?? FALLBACKS.cattleType,
    weightClass: watchlist?.weight_class ?? FALLBACKS.weightClass,
    region: (watchlist?.region as RegionOption) ?? FALLBACKS.region,
    ready: !isLoading,
  }), [watchlist?.cattle_type, watchlist?.weight_class, watchlist?.region, isLoading]);

  return (
    <DefaultFiltersContext.Provider value={value}>
      {children}
    </DefaultFiltersContext.Provider>
  );
}

/**
 * Returns the user's default filter values derived from their primary
 * watchlist. Falls back to "Steers" / "wt_600_699" / "All" if no
 * watchlist is set.
 *
 * Usage:
 *   const { cattleType, weightClass, region } = useDefaultFilters();
 *   const [ct, setCt] = useState(cattleType);
 */
export function useDefaultFilters(): DefaultFilters {
  return useContext(DefaultFiltersContext);
}
